import { Layer } from '../class/export';
import {
  MODE_DRAGGING_VERTEX,
  MODE_IDLE
} from '../constants';
import {
  IDBroker,
  GeometryUtils
} from '../utils/export';
import { Map, List } from 'immutable';
import { Vertex as VertexModel } from '../models';

class Vertex{

  static add( state, layerID, x, y, relatedPrototype, relatedID ) {

    let vertex = state.getIn(['scene', 'layers', layerID, 'vertices']).find(vertex => GeometryUtils.samePoints(vertex, {x, y}));

    if (vertex) {
      vertex = vertex.update(relatedPrototype, related => related.push(relatedID));
    }
    else {
      vertex = new VertexModel({
        id: IDBroker.acquireID(),
        name: 'Vertex',
        x, y,
        [relatedPrototype]: new List([relatedID])
      });
    }

    state = state.setIn(['scene', 'layers', layerID, 'vertices', vertex.id], vertex);

    return { updatedState: state, vertex };
  }

  static setAttributes(state, layerID, vertexID, vertexAttributes) {
    state = state.mergeIn(['scene', 'layers', layerID, 'vertices', vertexID ], vertexAttributes)

    return { updatedState: state };
  }

  // Clone a junction vertex so only one wall moves at a split (no stub wall).
  static detachForLine(state, layerID, lineID, vertexIndex) {
    const vertexID = state.getIn([
      "scene",
      "layers",
      layerID,
      "lines",
      lineID,
      "vertices",
      vertexIndex,
    ]);
    const vertex = state.getIn([
      "scene",
      "layers",
      layerID,
      "vertices",
      vertexID,
    ]);

    if (!vertex || vertex.lines.size <= 1) {
      return {
        updatedState: state,
        vertexID,
        originalVertexId: vertexID,
        detached: false,
      };
    }

    const originalVertexId = vertexID;

    const newVertex = new VertexModel({
      id: IDBroker.acquireID(),
      name: "Vertex",
      x: vertex.x,
      y: vertex.y,
      lines: new List([lineID]),
      areas: new List(),
    });

    state = Vertex.removeElement(
      state,
      layerID,
      vertexID,
      "lines",
      lineID
    ).updatedState;
    state = state.setIn(
      ["scene", "layers", layerID, "vertices", newVertex.id],
      newVertex
    );
    state = state.setIn(
      ["scene", "layers", layerID, "lines", lineID, "vertices", vertexIndex],
      newVertex.id
    );

    return {
      updatedState: state,
      vertexID: newVertex.id,
      originalVertexId,
      detached: true,
    };
  }

  static addElement( state, layerID, vertexID, elementPrototype, elementID ) {
    state = state.updateIn(['scene', 'layers', layerID, 'vertices', vertexID, elementPrototype], list => list.push( elementID ) );
    return { updatedState: state };
  }

  static removeElement( state, layerID, vertexID, elementPrototype, elementID ) {
    let elementIndex = state.getIn(['scene', 'layers', layerID, 'vertices', vertexID, elementPrototype]).findIndex( el => el === elementID );
    if( elementIndex !== -1 ) {
      state = state.updateIn(['scene', 'layers', layerID, 'vertices', vertexID, elementPrototype], list => list.remove( elementIndex ) );
    }
    return { updatedState: state };
  }

  static select( state, layerID, vertexID ){
    state = state.setIn(['scene', 'layers', layerID, 'vertices', vertexID, 'selected'], true);
    state = state.updateIn(['scene', 'layers', layerID, 'selected', 'vertices'], elems => elems.push(vertexID));

    return { updatedState: state };
  }

  static unselect( state, layerID, vertexID ){
    state = state.setIn(['scene', 'layers', layerID, 'vertices', vertexID, 'selected'], false);
    state = state.updateIn(['scene', 'layers', layerID, 'selected', 'vertices'], elems => elems.filter( el => el.id !== vertexID ));

    return { updatedState: state };
  }

  static remove( state, layerID, vertexID, relatedPrototype, relatedID, forceRemove ) {
    let vertex = state.getIn(['scene', 'layers', layerID, 'vertices', vertexID]);


    if( vertex ) {
      if( relatedPrototype && relatedID ) {
        vertex = vertex.update(relatedPrototype, related => {
          if (!related || typeof related.findIndex !== 'function') {
            return related || new List();
          }
          let index = related.findIndex(ID => relatedID === ID);
          if (index === -1) return related;
          return related.delete(index);
        });
      }

      const lineCount = vertex.lines && vertex.lines.size ? vertex.lines.size : 0;
      const areaCount = vertex.areas && vertex.areas.size ? vertex.areas.size : 0;
      let inUse = lineCount || areaCount;

      if( inUse && !forceRemove ) {
        state = state.setIn(['scene', 'layers', layerID, 'vertices', vertexID], vertex);
      }
      else {
        state = state.deleteIn(['scene', 'layers', layerID, 'vertices', vertexID]);
      }
    }

    return { updatedState: state };
  }

  static beginDraggingVertex(state, layerID, vertexID, x, y) {

    // No snap elements — free corner move for bay-window / angled walls.
    // Snap was pulling corners back onto H/V walls (client "snaps without angles").
    const vertex = state.getIn(['scene', 'layers', layerID, 'vertices', vertexID]);

    state = state.merge({
      mode: MODE_DRAGGING_VERTEX,
      snapElements: new List(),
      draggingSupport: Map({
        layerID,
        vertexID,
        startX: vertex ? vertex.x : x,
        startY: vertex ? vertex.y : y,
        previousMode: state.get('mode')
      })
    });

    return { updatedState: state };
  }

  static updateDraggingVertex(state, x, y) {
    let { draggingSupport, scene } = state;

    let layerID = draggingSupport.get('layerID');
    let vertexID = draggingSupport.get('vertexID');

    // Free corner move — allow bay-window / angled walls. No ortho lock, no snap.

    state =  state.merge({
      activeSnapElement: null,
      scene: scene.mergeIn(['layers', layerID, 'vertices', vertexID], { x, y })
    });

    return { updatedState: state };
  }

  static endDraggingVertex(state, x, y) {
    let { draggingSupport } = state;
    if (!draggingSupport) {
      return { updatedState: state };
    }
    let layerID = draggingSupport.get('layerID');
    let vertexID = draggingSupport.get('vertexID');

    // Commit free angled position. Do NOT recreate walls via
    // createAvoidingIntersections (auto-splits) or repairDiagonalWalls (kills angles).
    state = this.updateDraggingVertex(state, x, y).updatedState;

    state = Layer.mergeEqualsVertices(state, layerID, vertexID).updatedState;
    state = Layer.removeZeroLengthLines(state, layerID).updatedState;
    state = Layer.removeDuplicateLines(state, layerID).updatedState;
    state = Layer.detectAndUpdateAreas(state, layerID, {
      skipWeld: true,
    }).updatedState;
    state = Layer.ensureSolidRoomFill(state, layerID).updatedState;

    state = state.merge({
      mode: draggingSupport.get('previousMode') || MODE_IDLE,
      draggingSupport: null,
      activeSnapElement: null,
      snapElements: new List()
    });

    if (state.get('mode') === MODE_DRAGGING_VERTEX) {
      state = state.set('mode', MODE_IDLE);
    }

    return { updatedState: state };
  }

}

export { Vertex as default };
